import _ from 'underscore';
import { LabelsDataSource } from '@bingads-webui-campaign/labels';
import { getCommonFilterConfig } from '@bingads-webui-campaign/common-grid-config';
import { DimensionsShoppingSchemaProcessor, updateOdataOptionsForCampaignLanguages } from './filter-schema-processors/language-filter-processor';
import { DimensionsLocationSchemaProcessor } from './filter-schema-processors/location-filter-processor';
import { DimensionsCallForwardingSchemaProcessor } from './filter-schema-processors/call-forwarding-filter-processor';
import { DimensionsAgeAndGenderSchemaProcessor } from './filter-schema-processors/age-and-gender-filter-processor';
import { DimensionsPublisherWebsiteSchemaProcessor } from './filter-schema-processors/publisher-website-filter-processor';
import { DimensionsSearchTermsSchemaProcessor } from './filter-schema-processors/search-term-filter-processor';
import { filterOutCampaignAndAdgroupName } from './utils';

function getLabelId(value) {
  const newValue = JSON.parse(value);
  return newValue.Id ? newValue.Id : newValue;
}

function lambdaConvertForLabels(logic, operator) {
  return (values) => {
    let ids = '';

    _.each(values, (value, index) => {
      if (index > 0) {
        ids += ` ${logic} `;
      }
      ids += `LabelId ${operator} ${getLabelId(value)}`;
    });
    const url = `(${ids})`;
    return url;
  };
}

export function updateEntitySchemaForLabels({
  entitySchema,
  schemaI18nCampaign,
  i18n,
  cacheService,
  scope,
  dataContextManager,
  jsdata,
  edm,
}) {
  let entitySchemaCopy = entitySchema;
  entitySchemaCopy.properties.Labels = {
    property: 'Labels',
    name: schemaI18nCampaign.property('Campaign', 'Labels'),
    title: schemaI18nCampaign.property('Campaign', 'Labels'),
    operatorDisplayNameMap: {
      containsany: i18n.getString('FilterOperator_ContainsAny'),
    },
    operators: ['containsany'],
    type: 'id',
    addtionalParams: {
      dataSource: new LabelsDataSource({
        cacheService,
        dataContextManager,
        jsdata,
        scope,
        edm,
      }),
    },
    filterable: true,
  };

  entitySchemaCopy = filterOutCampaignAndAdgroupName(scope, entitySchemaCopy);
}

export function updateOdataOptionsForLabels(options) {
  if (options) {
    const optionsCopy = { custom: {} };

    _.extendOwn(optionsCopy.custom, {
      Labels: {
        containsany: lambdaConvertForLabels('or', 'eq'),
      },
    });
    return optionsCopy;
  }
  return options;
}
/*
a custom schema processor
both single account and multiple account dimensions MT accepts the originalProperty as filter key
e.g. MT accepts "Impressions" instead of "PerformanceMetrics/Impressions" or "PerformanceMetricsImpressions"
this custom schema processor returns the same schema but using originalProperty as key

below original property,
  PerformanceMetricsImpressions: <- replaced to Impressions
    filterable: true
    name: "PerformanceMetricsImpressions"
    type: "integer"
    property: "PerformanceMetrics/Impressions"
    originalProperty: "Impressions"
    ...
    alias:
      legacy: "Impressions"

is returned as below, only key is changed, all properties remain as is

  Impressions:
    filterable: true
    name: "PerformanceMetricsImpressions"
    type: "integer"
    property: "PerformanceMetrics/Impressions"
    originalProperty: "Impressions"
    ...
    alias:
      legacy: "Impressions"
*/
class DimensionsSchemaProcessor {
  constructor(options) {
    this.scope = options.scope;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    const updateEntitySchema = {
      properties: {
      },
    };

    _.each(entitySchema.properties, (schema) => {
      updateEntitySchema.properties[schema.originalProperty] = schema;
      if (this.scope.values.campaignId && updateEntitySchema.properties.CampaignName) {
        updateEntitySchema.properties.CampaignName.filterable = false;
      }
      if (this.scope.values.adGroupId && updateEntitySchema.properties.AdGroupName) {
        updateEntitySchema.properties.AdGroupName.filterable = false;
      }
    });

    updateEntitySchema.name = entitySchema.name;

    return updateEntitySchema;
  }
}

class DimensionsLablesSchemaProcessor {
  constructor(options) {
    this.i18n = options.i18n;
    this.schemaI18nCampaign = options.schemaI18nCampaign;
    this.dataContextManager = options.dataContextManager;
    this.cacheService = options.cacheService;
    this.jsdata = options.jsdata;
    this.scope = options.scope;
    this.edm = options.edm;
  }

  updateEntitySchemaForFeatures(entitySchema) {
    updateEntitySchemaForLabels({
      entitySchema,
      i18n: this.i18n,
      schemaI18nCampaign: this.schemaI18nCampaign,
      dataContextManager: this.dataContextManager,
      cacheService: this.cacheService,
      jsdata: this.jsdata,
      scope: this.scope,
      edm: this.edm,
    });
    return entitySchema;
  }

  updateOdataOptions(odataOptions) {
    updateOdataOptionsForLabels(odataOptions);
    return odataOptions;
  }
}

export function getFilterConfig({
  entitySchema,
  i18n,
  sessionStore,
  preferenceStoreFactory,
  legacyGlobalI18n,
  schemaI18nCampaign,
  cacheService,
  dataContextManager,
  preferenceService,
  appConfig,
  scope,
  jsdata,
  featureName,
  edm,
}) {
  let commonFeatureName = featureName;

  let dimensionsSchemaProcessor = new DimensionsSchemaProcessor({
    scope,
  });
  if (featureName.indexOf('Label') !== -1) {
    _.each(entitySchema.properties, (prop) => {
      // eslint-disable-next-line no-param-reassign
      prop.filterable = false;
    });
    const labelsEntitySchema = {
      properties: {
        Labels: {

        },
      },
    };

    dimensionsSchemaProcessor = new DimensionsLablesSchemaProcessor({
      entitySchema: labelsEntitySchema,
      i18n,
      schemaI18nCampaign,
      jsdata,
      dataContextManager,
      cacheService,
      scope,
      edm,
    });

    // use the same filter name for all dimensions labels pages
    // to share the saved filters.
    commonFeatureName = 'LabelReport';
  }

  if (featureName.indexOf('Shopping') !== -1) {
    dimensionsSchemaProcessor = new DimensionsShoppingSchemaProcessor({
      entitySchema,
      i18n,
      schemaI18nCampaign,
      jsdata,
      dataContextManager,
      cacheService,
      scope,
      globalConfig: appConfig,
    });
  }

  if (featureName.indexOf('CallForwardingDetailDimensionsGrid') !== -1) {
    dimensionsSchemaProcessor = new DimensionsCallForwardingSchemaProcessor({
      entitySchema,
      i18n,
      scope,
    });
  }

  if (featureName.indexOf('GeographicDimensionsGrid') !== -1) {
    dimensionsSchemaProcessor = new DimensionsLocationSchemaProcessor({
      entitySchema,
      i18n,
      scope,
    });
  }

  if (featureName.indexOf('AgeAndGenderDimensionsGrid') !== -1) {
    dimensionsSchemaProcessor = new DimensionsAgeAndGenderSchemaProcessor({
      entitySchema,
      i18n,
      scope,
    });
  }

  if (featureName.indexOf('PublisherWebsiteDimensionsGrid') !== -1) {
    dimensionsSchemaProcessor = new DimensionsPublisherWebsiteSchemaProcessor({
      entitySchema,
      i18n,
      scope,
    });
  }

  if (featureName.indexOf('SearchTermsDimensionsGrid') !== -1) {
    dimensionsSchemaProcessor = new DimensionsSearchTermsSchemaProcessor({
      entitySchema,
      i18n,
      scope,
    });
  }

  const filterConfig = getCommonFilterConfig({
    featureName: commonFeatureName,
    preferenceService,
    entitySchema,
    i18n,
    legacyGlobalI18n,
    sessionStore,
    preferenceStoreFactory,
    globalConfig: appConfig,
    schemaI18nCampaign,
    cacheService,
    dataContextManager,
    jsdata,
    editor: {},
    odata: {
      ignoreCase: false,
    },
    schemaProcessor: dimensionsSchemaProcessor,
    scope,
    edm,
  });

  return filterConfig;
}

// override odataOptionsProcessor and remove fieldMap from odataOptions
// with fieldMap, jsdata data source replaces query key to its property name
// e.g. "Impressions" => "PerformanceMetrics/Impressions"
// single-account dimensions query is different than most of other grids
// MT actually accepts "Impressions" so we don't need fieldMap for the extra translation
export const dimensionsFilterOdataOptionsProcessor = (odataOptions) => {
  // eslint-disable-next-line no-param-reassign
  odataOptions.fieldMap = null;

  // eslint-disable-next-line no-param-reassign
  odataOptions = updateOdataOptionsForLabels(odataOptions);
  return updateOdataOptionsForCampaignLanguages(odataOptions);
};
